import { useEffect, useMemo, useRef, useState } from "react";

import {
  Select,
  SelectArrow,
  SelectItem,
  SelectItemCheck,
  SelectLabel,
  SelectPopover,
  SelectState,
  useSelectState,
} from "ariakit/select";

import { SvgSprite, TooltipPopover } from "@skv/core";
import classNames from "classnames";
import { timingFunctions } from "polished";

type BaseItem = {
  id: string;
  name: string;
};

type LabelProps = React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  HTMLLabelElement
>;

type SelectProps = React.DetailedHTMLProps<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
>;

interface SelectMultiProps<T extends BaseItem> {
  id: string;
  label: string;
  labelProps?: LabelProps;
  selectProps?: SelectProps;
  name?: string;
  items: T[];
  defaultValue?: string | string[] | undefined;
  categoryTitle?: string;
  categoryType?: string;
  className?: string;
  onUpdateField: (value: string) => void;
  onClear?: () => void;
  onDropdownOpen?: () => void;
  renderValue?: (
    selectState: SelectState<string | string[]>,
    itemMap: Map<string, T>
  ) => React.ReactNode;
}

export const SelectMulti = <T extends BaseItem>({
  id,
  label,
  labelProps,
  selectProps,
  items,
  name,
  className,
  categoryTitle = "Nothing selected",
  categoryType = "items selected",
  onUpdateField,
  renderValue,
  onClear,
  onDropdownOpen,
  defaultValue = [],
}: SelectMultiProps<T>) => {
  const selectState = useSelectState<string | string[]>({
    value: defaultValue,
    sameWidth: true,
  });

  const itemMap = useMemo(() => {
    const map = new Map<string, T>();
    items.forEach((item) => {
      map.set(item.id, item);
    });

    return map;
  }, [items]);

  const selectMultiButtonRef = useRef<HTMLButtonElement>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  useEffect(() => {
    if (selectState.mounted) {
      onDropdownOpen && onDropdownOpen();
    }
  }, [selectState.mounted, onDropdownOpen]);

  return (
    <div className={className}>
      <SelectLabel
        htmlFor={id}
        as="label"
        className={labelProps?.className}
        state={selectState}
      >
        {label}
      </SelectLabel>
      <div className="position-relative bg-white rounded">
        <Select
          id={id}
          ref={selectMultiButtonRef}
          state={selectState}
          className={classNames(
            "d-flex select form-control overflow-hidden",
            selectProps?.className
          )}
          css={`
            align-items: center;
            justify-content: space-between;
            line-height: 2;

            white-space: nowrap;
            gap: 0.25em;
            border: 2px solid transparent;

            padding-right: ${onClear && selectState.value.length
              ? "2.75rem"
              : "0.75rem"};
          `}
        >
          <span>
            {(() => {
              if (renderValue) {
                return renderValue(selectState, itemMap);
              }
              if (selectState.value.length === 0) {
                return categoryTitle;
              }
              if (selectState.value.length === 1) {
                return itemMap.get(selectState.value[0])?.name;
              }
              return `${selectState.value.length} ${categoryType}`;
            })()}
          </span>

          <SelectArrow />
        </Select>
        {selectState.mounted && (
          <SelectPopover
            state={selectState}
            className="bg-white border py-1 overflow-auto shadow"
            css={`
              max-height: 300px;
              z-index: 50;
              display: flex;
              flex-direction: column;
              overscroll-behavior: contain;
              border-radius: 2px;
            `}
          >
            {items.map((x) => (
              <SelectItem
                key={x.id}
                value={x.id}
                className="d-flex align-items-center gap-2 p-2"
                onClick={() => {
                  onUpdateField(x.id);
                }}
                css={`
                  cursor: pointer;
                  line-height: 2;

                  &[data-active-item] {
                    background-color: #eee;
                  }
                `}
              >
                <SelectItemCheck />
                {x.name}
              </SelectItem>
            ))}
          </SelectPopover>
        )}
        {onClear && (
          // Note - this wrapping div is required for screens zoomed in >= 400% to prevent overflow from the clear button.
          <div
            className="position-absolute d-flex justify-content-start align-items-center"
            css={`
              top: 0;
              right: 0;
              bottom: 0;
              left: ${selectState.value.length
                ? "calc(100% - 2.75rem)"
                : "100%"};
              overflow: ${selectState.value.length ? "visible" : "hidden"};
              transition: all 300ms ${timingFunctions("easeOutCubic")};
            `}
          >
            <TooltipPopover
              placement="top"
              tooltipText="Clear"
              show={isTooltipVisible}
              onToggle={(nextShow) => setIsTooltipVisible(nextShow)}
            >
              <span
                role="button"
                className="btn text-muted bg-transparent border-0 p-0"
                tabIndex={selectState.value.length ? 0 : -1}
                css={`
                  opacity: ${selectState.value.length ? "1" : "0"};
                  transition: all 300ms ${timingFunctions("easeOutCubic")};

                  &:hover > .rounded-circle {
                    background-color: ${(p): string => p.theme.gray200};
                    box-shadow: 1px 1px 3px ${(p): string => p.theme.gray400};
                  }
                `}
                onClick={() => {
                  onClear();
                  selectMultiButtonRef.current?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    onClear();
                    setIsTooltipVisible(false);
                    selectMultiButtonRef.current?.focus();
                  }
                }}
              >
                <span
                  className="rounded-circle embed-responsive embed-responsive-1by1 p-3"
                  css={`
                    transition: all 300ms ${timingFunctions("easeOutCubic")};

                    &:hover {
                      background-color: ${(p): string => p.theme.gray200};
                      box-shadow: 1px 1px 3px ${(p): string => p.theme.gray400};
                    }
                  `}
                >
                  <SvgSprite
                    id="icons:close"
                    width={14}
                    height={14}
                    className="img-fluid embed-responsive-item p-2"
                    aria-label="Clear"
                  />
                </span>
              </span>
            </TooltipPopover>
          </div>
        )}
      </div>
      <input
        disabled={selectState.value.length === 0}
        type="hidden"
        name={name}
        value={selectState.value}
      />
    </div>
  );
};

export default SelectMulti;
